
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/useAuth";
import { CompanyMember } from "./CustomerFormTypes";

export function useCompanyMembers() {
  const [companyMembers, setCompanyMembers] = useState<CompanyMember[]>([]);
  const { user, profile } = useAuth();

  useEffect(() => {
    const fetchCompanyMembers = async () => {
      if (!profile?.company_id) {
        // If no company_id, at least add the current user
        if (user?.id) {
          const { data: currentUserProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();
            
          const currentUser: CompanyMember = {
            user_id: user.id,
            first_name: currentUserProfile?.first_name || profile?.first_name || 'Unknown',
            last_name: currentUserProfile?.last_name || profile?.last_name || 'User'
          };
          
          setCompanyMembers([currentUser]);
        }
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .eq('company_id', profile.company_id)
          .order('first_name');

        if (error) {
          console.error('Error fetching company members:', error);
          return;
        }

        const members = data?.map(profile => ({
          user_id: profile.id,
          first_name: profile.first_name || '',
          last_name: profile.last_name || ''
        })) || [];

        // Check if current user is already included
        const currentUserIncluded = members.some(member => member.user_id === user?.id);
        
        if (!currentUserIncluded && user?.id) {
          const { data: currentUserProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', user.id)
            .single();
            
          members.push({
            user_id: user.id,
            first_name: currentUserProfile?.first_name || profile?.first_name || 'Unknown',
            last_name: currentUserProfile?.last_name || profile?.last_name || 'User'
          });
        }

        setCompanyMembers(members);
      } catch (error) {
        console.error("Error in fetchCompanyMembers:", error);
      }
    };

    fetchCompanyMembers();
  }, [user, profile]);

  return { companyMembers };
}
